export enum TokenType {
  LParen = 'LParen',
  RParen = 'RParen',
  AND = 'AND',
  OR = 'OR',
  Filter = 'Filter',
  Bare = 'Bare',
  EOF = 'EOF',
}

export interface Token {
  type: TokenType;
  key?: string;
  value?: string;
}

/**
 * Tokenizes a search query string into syntax tokens.
 * Matches backend `internal/search/query.go` tokenization rules.
 */
export function tokenize(input: string, knownKeys?: Set<string> | string[]): Token[] {
  const keysSet = knownKeys ? (knownKeys instanceof Set ? knownKeys : new Set(knownKeys.map((k) => k.toLowerCase()))) : null;
  const tokens: Token[] = [];
  let i = 0;
  const len = input.length;

  while (i < len) {
    // Skip whitespace
    while (i < len && input[i] === ' ') {
      i++;
    }
    if (i >= len) break;

    // Parentheses
    if (input[i] === '(') {
      tokens.push({ type: TokenType.LParen });
      i++;
      continue;
    }
    if (input[i] === ')') {
      tokens.push({ type: TokenType.RParen });
      i++;
      continue;
    }

    // Read word or quoted string
    const start = i;
    while (i < len && input[i] !== ' ' && input[i] !== '(' && input[i] !== ')') {
      if (input[i] === '"') {
        i++; // skip opening quote
        while (i < len && input[i] !== '"') {
          if (input[i] === '\\' && i + 1 < len) {
            i++; // skip escape character
          }
          i++;
        }
        if (i < len && input[i] === '"') {
          i++; // skip closing quote
        }
      } else {
        i++;
      }
    }

    const word = input.slice(start, i);
    const upper = word.toUpperCase();

    if (upper === 'AND') {
      tokens.push({ type: TokenType.AND });
      continue;
    }
    if (upper === 'OR') {
      tokens.push({ type: TokenType.OR });
      continue;
    }

    const colonIdx = word.indexOf(':');
    if (colonIdx > 0) {
      const rawKey = word.slice(0, colonIdx).toLowerCase();
      // If knownKeys is provided, check membership; otherwise accept any key:value token
      if (!keysSet || keysSet.has(rawKey)) {
        let val = word.slice(colonIdx + 1);
        if (val.length >= 2 && val.startsWith('"') && val.endsWith('"')) {
          val = val.slice(1, -1);
        }
        tokens.push({ type: TokenType.Filter, key: rawKey, value: val });
        continue;
      }
    }

    let bareVal = word;
    if (bareVal.length >= 2 && bareVal.startsWith('"') && bareVal.endsWith('"')) {
      bareVal = bareVal.slice(1, -1);
    }
    tokens.push({ type: TokenType.Bare, value: bareVal });
  }

  tokens.push({ type: TokenType.EOF });
  return tokens;
}
